<template>
  <v-container class="question-container" v-if="question !== undefined">
    <div class="row">
      <v-col cols="6">
        <v-breadcrumbs :items="breadcrumbs" divider=">"></v-breadcrumbs>
        <v-chip light :color="pointColor" :label="true" :append="true">{{question.pointValue}} pts</v-chip>
        <h3 v-if="question.oneDone" class="d-inline one-done">One & Done</h3>
      </v-col>
      <v-col>
        <div class="audio-player" v-if="question.audioUrl">
          <audio :src="'https://storage.googleapis.com/mmpof3.appspot.com/audio/' + question.audioUrl" type="audio/mpeg" controls="controls"></audio>
        </div>
      </v-col>
    </div>
    <v-row>
      <v-col cols="12">
        <div class="headline">
          <v-btn icon text color="grey darken-1" @click="copyQuestionText">
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
          {{question.text}}
          <v-btn icon text color="grey darken-1" :to="`/hour/${$route.params.id}/question/${$route.params.key}/edit`">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="9">
        <v-card v-if="!question.open || question.oneDoneSubmitted" outlined :class="pointColor">
          <v-card-text>
            <p class="title">{{ question.solution.text }}</p>
            <p class="subtitle-2" v-show="question.solution.submittedBy">
              Submitted By: <span class="subtitle-1">{{ question.solution.submittedBy }}</span> @ {{ formatTime(question.solution.submittedAt) }}
            </p>
            <ul>
              <li>Recorded By: <span class="subtitle-1">{{ question.createdBy }}</span></li>
              <li>
                Called In By: <span class="subtitle-1">{{ question.solution.calledInBy }}</span> @ {{ formatTime(question.solution.calledInAt) }}
              </li>
              <li> Phone Bank Worker: {{ question.solution.phoneBankWorker }}</li>
            </ul>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="toggleTeamEffort(question.teamEffort)">Team Effort</v-btn>
            <v-icon id="team" large v-if="question.teamEffort">mdi-account-multiple-check</v-icon>
          </v-card-actions>
        </v-card>
        <v-row  v-if="question.open && !question.oneDoneSubmitted">
          <v-col cols=11>
            <form action="#">
              <v-textarea
                id="answer"
                name="answer"
                ref="answer"
                v-model="text"
                outlined
                autofocus
                placeholder="enter answer here"
                tabindex="1"
                cols="20"
                rows="6">
              </v-textarea>
            </form>
          </v-col>
          <v-col cols="1">
            <span>Submit</span>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn tile color="warning" tabindex="2" @click="addAnswer(text, 3)" v-on="on">3</v-btn>
              </template>
              <span>Worth a guess</span>
            </v-tooltip>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn tile color="primary" tabindex="3" @click="addAnswer(text, 2)" v-on="on">2</v-btn>
              </template>
              <span>Researched guess</span>
            </v-tooltip>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn tile color="success" tabindex="4" @click="addAnswer(text, 1)" v-on="on">1</v-btn>
              </template>
              <span>This is it!</span>
            </v-tooltip>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn tile light tabindex="5" @click="addNote(text)" v-on="on">Note</v-btn>
              </template>
              <span>Add as note</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="3">
        <v-card v-if="!!question.visual">
          <v-img :src="question.visual.thumbnailFileName" contain max-height="200" />
          <v-card-title>Visual #{{ question.visual.visualNumber }}</v-card-title>
          <v-card-actions>
            <v-btn text primary @click="openFull(question.visual.forumLink)" class="float-right">
              Forum
              <v-icon x-small color="gray">mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn text primary @click="openFull(question.visual.fileName)">
              Full Img
              <v-icon x-small color="gray darken-2">mdi-open-in-new</v-icon>
            </v-btn>
            <v-chip v-if="question.visual.found" color="green" :label="true" :append="true">Found</v-chip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="answers !== undefined" class="answer-list">
      <v-col :lg="4" :md="6" :sm="10">
        <h3 :class="guessThresholdStyle(submissions.length)">{{ submissions.length }} Submissions</h3>
        <v-simple-table v-if="answers.submissions !== undefined">
          <template v-slot:default>
            <thead>
            <tr dense>
              <th>Answer</th>
              <th>Researcher</th>
              <th>#</th>
            </tr>
            </thead>
            <tbody v-if="answers.submissions !== undefined">
            <tr v-for="(sub, index) in submissions" :key="index">
              <td>{{ sub.text }}</td>
              <td>{{ sub.submittedBy }}</td>
              <td>
                {{ sub.sureness }}
                <v-icon dark small @click="lessSure(sub)" v-if="sub.sureness < 4 && sub.submittedBy == me.username">mdi-arrow-down-box</v-icon>
              </td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
      <v-col :lg="4" :md="6" :sm="10">
        <h3>Notes & Partials</h3>
        <v-simple-table v-if="answers !== undefined">
          <template v-slot:default>
            <thead>
            <tr>
              <th>Answer</th>
              <th>Researcher</th>
              <th><v-icon>mdi-swap-vertical</v-icon></th>
            </tr>
            </thead>
            <tbody v-if="answers.notes!== undefined">
            <tr v-for="(sub, index) in notes" :key="index">
              <td :class="noteClass(sub)">
                <v-chip v-if="sub.partial" class="pill" color="primary" :label="true" :append="true">partial</v-chip>
                <span v-html="linkify(sub.text)"></span>
              </td>
              <td :class="noteClass(sub)">{{ sub.submittedBy }}</td>
              <td>
                <v-icon x-small @click="noteMoreRelevant(sub)" v-if="sub.relevance > 1">
                  mdi-arrow-up-thick
                </v-icon>
                <v-icon x-small @click="noteLessRelevant(sub)" v-if="sub.relevance < 3">
                  mdi-arrow-down-thick
                </v-icon>
              </td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
      <v-col :lg="4" :md="6" :sm="10">
        <h3>Incorrect</h3>
        <v-simple-table v-if="answers.incorrects!== undefined">
          <template v-slot:default>
            <thead>
            <tr>
              <th>Answer</th>
              <th>Researcher</th>
            </tr>
            </thead>
            <tbody v-if="answers.incorrects!== undefined">
            <tr v-for="(sub, index) in answers.incorrects" :key="index">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <td v-on="on">{{ sub.text }}</td>
                </template>
                <span><v-icon>mdi-phone</v-icon> {{sub.calledInBy}}</span><br>
                <span>{{new Date(sub.calledInAt).toLocaleTimeString()}}</span>
              </v-tooltip>
              <td>{{ sub.submittedBy }}</td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else>
    <v-skeleton-loader
      type="paragraph@4, card"
    />
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { map, sortBy, orderBy, filter } from 'lodash'
import bus from '@/plugins/event-bus'

export default {
  data () {
    return {
      text: '',
      breadcrumbs: [],
      pointColor: 'default',
      submissionThresholds: {},
    }
  },
  beforeRouteEnter (to, from, next) {
    document.title = 'Q' + to.params.key + ' | Meyerbase'
    next()
  },
  computed: {
    question () {
      return this.$store.getters['questions/getQuestion'](this.$route.params.id, this.$route.params.key)
    },
    answers () {
      return this.$store.getters['answers/getQuestionAnswers'](this.$route.params.id, this.$route.params.key)
    },
    submissions () {
      let submissionsNotDeleted = filter(this.answers.submissions, sub => !sub.toBeDeleted)
      return sortBy(submissionsNotDeleted, 'sureness')
    },
    notes () {
      let notesWithKey = map(this.answers.notes, (item, key) => ({ ...item, _key: key }))
      return orderBy(notesWithKey, ['relevance'], ['asc'])
    },
    questionKey: function () {
      return this.$store.getters['questions/getQuestionKey'](this.$route.params.id, this.$route.params.key)
    },
    ...mapState({
      me: state => state.me,
    }),
  },
  mounted: async function () {
    await this.$store.dispatch('questions/bindHourQuestions', this.$route.params.id)
    await this.$store.dispatch('answers/bindQuestionAnswers', {
      hourNumber: this.$route.params.id,
      questionNumber: this.$route.params.key,
    })
    await this.$store.dispatch('answers/bindHourAnswers', this.$route.params.id)
    await this.$store.dispatch('siteSettings/bindSiteSettings')
    this.submissionThresholds = this.$store.getters['siteSettings/getSubmissionThresholds']()
    this.setColor(this.question)
    this.updatePageTitle(this.question.open)
    this.addBreadcrumbs()
    bus.$emit('question-opened', { question: this.question, questionKey: this.questionKey })
  },
  beforeDestroy: function () {
    bus.$emit('question-closed')
  },
  watch: {
    question: function (newQ, oldQ) {
      if (newQ && oldQ && newQ.questionNumber !== oldQ.questionNumber) {
        bus.$emit('question-opened', { question: this.question, questionKey: this.questionKey })
        this.text = ''
        this.$store.dispatch('answers/bindQuestionAnswers', {
          hourNumber: this.$route.params.id,
          questionNumber: this.$route.params.key,
        })
      }
      if (newQ && oldQ && newQ.open !== oldQ.open) {
        this.updatePageTitle(newQ.open)
      }
      this.addBreadcrumbs()
      this.setColor(newQ)
    },
  },
  methods: {
    addAnswer: function (text, sureness) {
      if (typeof text !== 'undefined' && text.trim() !== '') {
        let answer = {
          text: text.trim(),
          sureness: parseInt(sureness),
          submittedBy: this.me.username,
          hourNumber: this.question.hourNumber,
          questionNumber: this.question.questionNumber,
          questionId: this.$route.params.key,
        }
        let submissionDuplicates = this.getDuplicates(answer, this.answers.submissions)
        if (!this.answers || !this.answers.submissions ||
            this.getDuplicates(answer, this.answers.incorrects).length <= 0) {
          if (submissionDuplicates.length > 0) {
            let submission = submissionDuplicates[0]
            let updatedProps = {}
            if (answer.sureness < submission.sureness) {
              updatedProps.sureness = answer.sureness
              if (answer.submittedBy !== submission.submittedBy) {
                updatedProps.submittedBy = 'Team'
              }
              this.$store.dispatch('answers/updateAnswer', {
                hourNumber: this.question.hourNumber,
                questionNumber: this.question.questionNumber,
                answer: submission,
                newProps: updatedProps,
              })
              this.resetAnswerInput()
            } else {
              alert('Duplicate answer.\n\nHit Enter to clear.')
            }
          } else {
            this.$store.dispatch('answers/add', {
              hourNumber: this.question.hourNumber,
              questionNumber: this.question.questionNumber,
              answer: answer,
            })
            this.resetAnswerInput()
          }
        } else {
          alert('Answer already called in and incorrect.\n\nHit Enter to clear.')
        }
      }
    },
    addNote: function (text) {
      if (typeof text !== 'undefined' && text.trim() !== '') {
        let answer = {
          text: text,
          sureness: 'note',
          relevance: 1,
          submittedBy: this.me.username,
          hourNumber: this.question.hourNumber,
          questionNumber: this.question.questionNumber,
          questionId: this.$route.params.key,
        }
        if (!this.answers || this.getDuplicates(answer, this.answers.notes).length <= 0) {
          let submission = {
            hourNumber: this.question.hourNumber,
            questionNumber: this.question.questionNumber,
            answer: answer,
          }

          this.$store.dispatch('answers/addNote', submission)
          this.resetAnswerInput()
        } else {
          alert('duplicate note')
        }
      }
    },
    toggleTeamEffort: function (teamEffort) {
      this.$store.dispatch('questions/toggleTeamEffort', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        effort: !teamEffort,
      })
    },
    getDuplicates: function (answer, manyAnswers) {
      let dupsArray = filter(manyAnswers, (ans) => {
        if (typeof ans.text === 'undefined') {
          return false
        }
        return ans.text.trim().toUpperCase() === this.text.trim().toUpperCase()
      })
      return dupsArray
    },
    resetAnswerInput: function () {
      this.text = ''
      this.sureness = 2
      this.$refs.answer.focus()
    },
    addBreadcrumbs: function (theQuestion) {
      this.breadcrumbs = [
        { text: 'Home', to: '/' },
        { text: `Hour ${this.$route.params.id}`, to: `/hour/${this.$route.params.id}`, exact: true },
        { text: `Question ${this.$route.params.key}` },
      ]
    },
    setColor: function (question) {
      if (!question.open) {
        if (question.correct) {
          this.pointColor = 'success'
        } else {
          this.pointColor = 'error'
        }
      } else if (question.oneDoneSubmitted) {
        this.pointColor = 'deep-purple lighten-1'
      } else {
        this.pointColor = ''
      }
    },
    formatTime: function (timestamp) {
      let date = new Date(timestamp)
      return date.toLocaleString()
    },
    linkify: function (text) {
      const regex = /(https?:\/\/[^\s/$.?#].[^\s]*)/gm
      let replacement = text.replace(regex, '<a href="$1" target="blank">$1</a>')
      return replacement
    },
    openFull (path) {
      window.open(path, '_blank')
    },
    updatePageTitle (isOpen) {
      if (isOpen) {
        document.title = '[OPEN] Q' + this.$route.params.key + ' | Meyerbase'
      } else {
        document.title = '[CLOSED] Q' + this.$route.params.key + ' | Meyerbase'
      }
    },
    lessSure (answer) {
      this.$store.dispatch('answers/updateAnswer', {
        hourNumber: this.question.hourNumber,
        questionNumber: this.question.questionNumber,
        answer: answer,
        newProps: { sureness: answer.sureness + 1 },
      })
    },
    noteLessRelevant (note) {
      this.$store.dispatch('answers/updateNoteRelevance', {
        hourNumber: this.question.hourNumber,
        questionNumber: this.question.questionNumber,
        answerKey: note._key,
        relevance: note.relevance + 1,
      })
    },
    noteMoreRelevant (note) {
      this.$store.dispatch('answers/updateNoteRelevance', {
        hourNumber: this.question.hourNumber,
        questionNumber: this.question.questionNumber,
        answerKey: note._key,
        relevance: note.relevance - 1,
      })
    },
    noteClass (note) {
      if (note.relevance === 2) return 'questionable'
      else if (note.relevance === 3) return 'irrelevant'
      else return ''
    },
    guessThresholdStyle (number) {
      if (number >= this.submissionThresholds.high) return 'red--text text--lighten-1'
      else if (number >= this.submissionThresholds.medium) return 'orange--text'
      else if (number >= this.submissionThresholds.low) return 'yellow--text text--lighten-1'
      else return ''
    },
    copyQuestionText () {
      navigator.clipboard.writeText(this.question.text)
    },
  },
}
</script>

<style scoped>
  .question-container {
    padding: 6px;
  }
  .v-breadcrumbs {
    display: inline-block;
  }
  .v-chip {
    display: inline-block;
    margin-right: 5px;
  }
  .questionable {
    font-style: italic;
  }
  .irrelevant {
    text-decoration: line-through;
    color: gray;
    .pill {
      background-color: grey !important;
    }
  }
  .answer-list {
    padding-bottom: 100px;
  }
  .one-done {
    padding: 1rem;
    color: #7E57C2;
  }
  #team {
    margin-left: 10px;
  }
  td {
    word-break: break-all;
  }
</style>
